// nombresBitacora.ts — los nombres en español de todo lo que el bot anota en la bitácora (round 2026-10-30, etapa B3c).
//
// POR QUÉ ESTÁ APARTE. La bitácora (tabla `bot_eventos`, la escribe el servicio twilio y la entrega GET /api/bot/eventos) trae
// identificadores internos: menús («rate-limited», «unauthenticated»), códigos («GUARDADO_SIMULADO», «LIMITE_TELEFONO»),
// pasos de la conversación («session_started») y claves del detalle («guardadoEnEsteTurno»). Un dueño que no es técnico no
// tiene por qué conocerlos: este archivo es el ÚNICO mapa y la pantalla nunca muestra uno de estos identificadores crudos si
// lo conoce. Lo que no conoce se muestra tal cual entre paréntesis (mejor feo y honesto que un nombre inventado).
//
// DE DÓNDE SALEN LOS VALORES. Solo hay valores que existen de verdad en el código:
//   · twilio/src/webhook.js, turno.js, etapa_cero.js, gasto.js, simulador.js, libs/bloqueosAgregados.js y libs/clienteBackend.js
//     (lo que el bot escribe: códigos, menús de salida y los tipos de fallo del servidor en mayúsculas);
//   · backend/src/controllers/bot.controller.js, services/bot*.js, utils/botSignature.js, routes/bot.routes.js,
//     middleware/botSignature.middleware.js, utils/backupUsuarios.js y services/gastosEmpresa.service.js (los códigos con los que el
//     servidor rechaza una operación del bot o un restore);
//   · backend/src/services/botEventos.js (las listas CERRADAS con las que el servidor sanea la bitácora antes de entregarla).
// NO SE PUEDE QUEDAR ATRASADO EN SILENCIO: `nombresBitacora.antiDeriva.test.ts` lee esos archivos y falla si el bot o el servidor
// estrenan un código, un menú, un paso o una clave del detalle sin darle nombre acá. Regla para quien agregue uno: agrégalo
// aquí, con un nombre claro y sin tecnicismos, en el mismo cambio.
import type { BotEventoDetalle } from "../../../interfaces/botAsistente.interfaces";

/** Cómo se llama lo que el servidor no pudo clasificar: manda «otro» (su juego cerrado) en lugar del valor original. */
export const NOMBRE_OTRO = "Otro (no reconocido)";

const tiene = (mapa: Record<string, string>, clave: string): boolean => Object.prototype.hasOwnProperty.call(mapa, clave);

// ─── Menús ───────────────────────────────────────────────────────────────────

/**
 * Los menús que anota la bitácora: los de verdad (de `menuHandlers` en twilio/src/menu_handlers.js) y los «menús» con los que
 * el turno marca lo que no fue un menú (twilio/src/turno.js y webhook.js). Cada uno de la lista cerrada de
 * backend/src/services/botEventos.js (`MENUS_CONOCIDOS`) tiene su nombre.
 */
export const NOMBRES_MENU: Readonly<Record<string, string>> = {
  gasto: "Gastos de la empresa",
  entrada: "Interpretación del mensaje",
  fotos: "Fotos adjuntas",
  compra: "Compras de inventario",
  movimiento: "Traslados de inventario",
  lavanderia: "Lavandería",
  consulta: "Consulta de inventario",
  ayuda: "Ayuda",
  cancelar: "Cancelar",
  cancel: "Operación cancelada",
  "etapa-cero-error": "Error al leer el mensaje",
  "opcion-retirada": "Opción retirada",
  "respuesta-directa": "Respuesta directa",
  "respuesta-invalida": "Respuesta no válida",
  silencio: "Sin respuesta (a propósito)",
  "unknown-option": "Opción no reconocida",
  // Los bloqueos de quien no es del equipo (se anotan juntos, uno por ventana de 5 minutos; twilio/src/libs/bloqueosAgregados.js):
  duplicate: "Mensaje repetido (ya procesado)",
  "rate-limited": "Demasiados mensajes",
  // Cubre al número que no está registrado, al registrado con un rol que no usa el asistente y al teléfono que no se pudo leer: por eso no dice «no registrado».
  unauthenticated: "Número sin acceso al asistente",
  "remitente-ambiguo": "Número en más de un usuario",
  "adjunto-no-soportado": "Adjunto no soportado",
  otro: NOMBRE_OTRO,
};

/** Con estos prefijos el bot marca un menú que NO se ejecutó (`denied:gasto`, `apagado:compra`). */
export const PREFIJOS_MENU: Readonly<Record<string, string>> = {
  "denied:": "Menú sin permiso",
  "apagado:": "Menú apagado",
};

/**
 * Los menús por los que se puede filtrar la actividad: los que de verdad se escriben tal cual en la bitácora. Al cancelar una conversación el bot anota
 * `cancel` (twilio/src/turno.js: «Operación cancelada.»); `cancelar` es el nombre de un manejador que ningún turno escribe, así que filtrar por él
 * nunca encontraba nada.
 */
export const MENUS_FILTRO = ["gasto", "compra", "movimiento", "lavanderia", "consulta", "ayuda", "cancel"] as const;

/** Nombre en español de un menú. «—» si no hay; el valor crudo entre paréntesis si no se conoce. */
export function textoMenu(menu: string | null | undefined): string {
  if (typeof menu !== "string" || menu === "") return "—";
  if (tiene(NOMBRES_MENU, menu)) return NOMBRES_MENU[menu];
  for (const prefijo of Object.keys(PREFIJOS_MENU)) {
    if (!menu.startsWith(prefijo)) continue;
    const base = menu.slice(prefijo.length);
    // Un nombre compuesto («Menú sin permiso: Gastos de la empresa»); el menú de atrás se busca una sola vez (sin encadenar prefijos).
    const nombre = base === "" ? "—" : tiene(NOMBRES_MENU, base) ? NOMBRES_MENU[base] : `(${base})`;
    return `${PREFIJOS_MENU[prefijo]}: ${nombre}`;
  }
  return `(${menu})`;
}

// ─── Pasos de la conversación (sub_estado) ───────────────────────────────────

/** Los pasos de la lista cerrada de backend/src/services/botEventos.js (`SUB_ESTADOS_CONOCIDOS`), de twilio/src/libs/sessionState.js. */
export const NOMBRES_SUB_ESTADO: Readonly<Record<string, string>> = {
  procesando: "Leyendo el mensaje",
  guardando: "Guardando",
  confirmar: "Esperando confirmación (1, 2 o 3)",
  // El valor histórico de «confirmar»: significa lo mismo (sessionState.js).
  session_started: "Esperando confirmación (1, 2 o 3)",
  // Las preguntas del gasto (twilio/src/gasto.js): la respuesta de la persona es el próximo mensaje.
  awaiting_field_correction: "Esperando la respuesta a una pregunta (monto, fecha, propiedad…)",
  await_reembolso_choice: "Esperando que elija de una lista (con el número)",
  // Menús antiguos (compra, traslado, consulta): están apagados, pero una sesión vieja puede seguir en uno de estos pasos.
  await_consulta_choice: "Esperando que elija un producto (consulta antigua)",
  await_choices: "Esperando que elija opciones (compra o traslado antiguo)",
  await_cargo_choice: "Esperando a quién se le carga el costo (traslado antiguo)",
  await_similarity: "Procesando la confirmación",
  otro: NOMBRE_OTRO,
};

/** Nombre en español del paso de la conversación. «—» si no hay; el valor crudo entre paréntesis si no se conoce. */
export function textoSubEstado(subEstado: string | null | undefined): string {
  if (typeof subEstado !== "string" || subEstado === "") return "—";
  return tiene(NOMBRES_SUB_ESTADO, subEstado) ? NOMBRES_SUB_ESTADO[subEstado] : `(${subEstado})`;
}

// ─── Quién escribió (rol) ────────────────────────────────────────────────────

/** Los roles de la lista cerrada de backend/src/services/botEventos.js (`ROLES_EVENTO`). */
export const NOMBRES_ROL: Readonly<Record<string, string>> = {
  superadministrador: "Superadministrador",
  administrador: "Administrador",
  aseador: "Aseador",
  cliente: "Cliente",
  trabajador: "Trabajador",
  propietario: "Propietario",
  otro: NOMBRE_OTRO,
};

export function textoRol(rol: string | null | undefined): string {
  if (typeof rol !== "string" || rol === "") return "—";
  return tiene(NOMBRES_ROL, rol) ? NOMBRES_ROL[rol] : `(${rol})`;
}

// ─── Códigos ─────────────────────────────────────────────────────────────────

/**
 * Los códigos del motivo de un evento, agrupados por quién los escribe. El texto es corto y sin tecnicismos: lo lee un dueño
 * que no es técnico. Un código que no está acá se muestra tal cual entre paréntesis (ver `textoCodigo`).
 */
const CODIGOS_DEL_ASISTENTE: Record<string, string> = {
  // Cómo terminó un gasto (twilio/src/turno.js, `clasificarTurno`)
  GUARDADO: "Gasto guardado",
  GUARDADO_SIMULADO: "Gasto simulado (no se guardó)",
  DESHECHO: "Gasto deshecho",
  DESHECHO_SIMULADO: "Simulación deshecha (no había nada guardado)",
  CANCELADO: "Cancelado por la persona",
  // Mensajes que no hacían falta o que el asistente contestó con un texto fijo
  MENSAJE_VACIO: "Mensaje vacío",
  CORTESIA: "Mensaje de cortesía (gracias, ok…)",
  RESPUESTA_DIRECTA: "Respuesta fija del asistente",
  OCUPADO_SILENCIO: "Sin respuesta: había otra operación en curso",
  OPCION_RETIRADA: "Opción retirada (ya no existe)",
  OPCION_DESCONOCIDA: "Opción no reconocida",
  SOLO_REINICIO: "Solo se reinició la conversación de prueba",
  // Lo que no se pudo hacer por permisos o porque la función está apagada
  SIN_PERMISO: "Sin permiso para esa función",
  FUNCION_NO_DISPONIBLE: "Función apagada por ahora",
  FUNCION_RETIRADA: "Función retirada (ya no existe por WhatsApp)",
  BOT_EN_MANTENIMIENTO: "El sistema estaba restaurando una copia de seguridad: no se guardó nada",
  MODO_NO_VERIFICADO: "No se pudo comprobar el modo: no se guardó nada",
  MODO_CAMBIO_A_REAL: "El modo pasó a real mientras esperaba: se descartó",
  // Quién escribió (twilio/src/webhook.js)
  LIMITE_TELEFONO: "Demasiados mensajes seguidos",
  SID_DUPLICADO: "Mensaje repetido (ya procesado)",
  REMITENTE_DESCONOCIDO: "Número no registrado o sin permiso",
  REMITENTE_AMBIGUO: "Número registrado en más de un usuario",
  ADJUNTO_NO_SOPORTADO: "Adjunto no soportado (audio, video o documento)",
  FOTOS: "Fotos recibidas",
  LIMITE_FOTOS: "Se excedió el límite de fotos",
  LAVANDERIA_INVALIDA: "Los datos de lavandería no son válidos",
  FOTOS_NO_GUARDADAS: "No se pudieron guardar todas las fotos",
  // Fallos
  ERROR_INESPERADO: "Error inesperado",
  ENVIO_FALLIDO: "No se pudo enviar la respuesta por WhatsApp",
  ETAPA_CERO: "Falló al interpretar el mensaje",
  HANDLER_FALLO: "Falló al procesar el pedido",
  MODELO_NO_RESPONDIO: "La inteligencia artificial no respondió",
  MODELO_RESPALDO: "Usó el modelo de respaldo (el elegido en el panel no existe)",
  RESPUESTA_INVALIDA: "Respuesta que el asistente no pudo interpretar",
};

/** Cuando el servidor rechaza sin un código propio, el asistente anota el TIPO de fallo en mayúsculas (twilio/src/libs/clienteBackend.js). */
const CODIGOS_DE_TIPO_DE_FALLO: Record<string, string> = {
  VALIDACION: "El servidor rechazó los datos",
  AUTH: "El servidor no reconoció al asistente",
  CONFLICTO: "Choque con una operación anterior",
  DEMASIADO_GRANDE: "La solicitud era demasiado grande",
  FORMATO: "El servidor no aceptó el formato",
  LIMITE: "Demasiadas solicitudes al servidor",
  DESACTIVADO: "El asistente está desactivado en el servidor",
  SERVIDOR: "El servidor falló",
  NO_CONFIGURADO: "Falta configurar la conexión con el servidor",
  RED: "No hubo conexión con el servidor",
  TIMEOUT: "El servidor tardó demasiado en responder",
  BACKEND: "El servidor rechazó la operación (sin detalle)",
};

/** Con estos códigos responde el servidor (backend/src/controllers/bot.controller.js, services/bot*.js, utils/botSignature.js y routes/bot.routes.js). */
const CODIGOS_DEL_SERVIDOR: Record<string, string> = {
  FOTO_INVALIDA: "La foto no tiene un formato válido",
  REGISTRO_INVALIDO: "El registro elegido para la foto no es válido",
  MODO_SIMULACION: "Operación de prueba: no se guardó la foto real",
  FIRMA_INVALIDA: "La solicitud del asistente no venía bien firmada",
  HORA_FUERA_DE_RANGO: "La hora de la solicitud del asistente no coincide con la del servidor",
  ACTOR_SIN_PERMISO: "Quien escribió no es administrador",
  CLAVE_REUTILIZADA: "Esa operación ya se había usado con otro contenido",
  OPERACION_DESHECHA: "Esa operación ya se había deshecho",
  OPERACION_EN_CURSO: "Esa operación todavía se estaba procesando",
  NO_ES_TUYO: "La operación la registró otra persona",
  YA_DESHECHA: "La operación ya estaba deshecha",
  FUERA_DE_PLAZO: "Pasaron más de 10 minutos: ya no se puede deshacer por WhatsApp",
  MODIFICADO: "El gasto cambió después de guardarse y ya no se puede anular desde aquí",
  DERIVADO_DE_INVENTARIO: "El gasto quedó ligado a una compra de inventario y no se puede anular desde aquí",
  NO_EXISTE: "La operación no existe",
  LIMITE_EXCEDIDO: "Demasiadas solicitudes seguidas: había que esperar un minuto",
  ERROR_INTERNO: "El servidor falló al procesar la solicitud",
  CONTRATO: "La solicitud del asistente no tenía el formato esperado",
  FEATURE_DISABLED: "El asistente está desactivado en el servidor (falta configurarlo)",
  METODO: "Método no permitido (solo se acepta POST)",
  // El cuerpo de la solicitud (backend/src/middleware/botSignature.middleware.js)
  CUERPO: "La solicitud del asistente traía datos que el servidor no pudo leer",
  CUERPO_DEMASIADO_GRANDE: "La solicitud del asistente era demasiado grande",
  CUERPO_ILEGIBLE: "No se pudo leer el contenido de la solicitud del asistente",
  TIPO_CONTENIDO_INVALIDO: "La solicitud del asistente no venía en el formato que el servidor acepta",
  CODIFICACION_NO_SOPORTADA: "La solicitud del asistente venía comprimida y el servidor no la acepta",
  // Lo que pide el panel (esta pantalla) y el diagnóstico, no el asistente de WhatsApp
  CONFIRMACION_REQUERIDA: "Falta escribir la frase de confirmación",
  TAMANO_INVALIDO: "El tamaño de página no es válido",
  TEXTO_INVALIDO: "El texto de búsqueda no es válido",
  PAGINA_INVALIDA: "Esa página no existe",
  TOKEN_INVALIDO: "El acceso de diagnóstico no es válido",
  DIAGNOSTICO_APAGADO: "El diagnóstico está apagado",
  // Lo que responde al restaurar o importar una copia de seguridad (pantalla Backup); no pasa por la bitácora del asistente, pero el servidor los nombra.
  ESCRITURAS_DEL_BOT_EN_CURSO: "No se restauró nada: el asistente de WhatsApp estaba guardando algo",
  SIN_SUPERADMINISTRADOR: "La copia dejaría al sistema sin superadministrador: no se aplicó nada",
};

/** Con estos códigos rechaza el servidor el CONTENIDO de un gasto (backend/src/services/gastosEmpresa.service.js). */
const CODIGOS_DE_VALIDACION_DEL_GASTO: Record<string, string> = {
  LOTE_INVALIDO: "No había gastos que guardar, o eran demasiados a la vez",
  MONTO_INVALIDO: "El monto no es válido",
  FECHA_INVALIDA: "La fecha no es válida",
  CATEGORIA_INVALIDA: "La categoría no es válida",
  DESCRIPCION_INVALIDA: "Falta la descripción o no es válida",
  PROPIEDAD_NO_EXISTE: "La propiedad no existe",
  PROPIEDAD_DESHABILITADA: "La propiedad está deshabilitada",
  PROPIEDAD_REQUERIDA: "Falta indicar la propiedad",
  TIPO_INVALIDO: "El tipo de movimiento no es válido (esporádico o regular)",
  FECHA_PERIODICA_INVALIDA: "El día del mes en que se repite el movimiento no es válido",
  VIGENCIA_INVALIDA: "La vigencia del movimiento no es válida",
  NOMBRE_INVALIDO: "El nombre del movimiento no es válido",
  ROL_INVALIDO: "Falta indicar si Aloja Fácil paga, recibe o intermedia",
  PUNTA_INVALIDA: "Quién paga o a quién se le paga no es válido",
  ASEADOR_REQUERIDO: "Falta indicar de qué aseadora se trata",
  PERSONA_NO_EXISTE: "La persona indicada no existe",
  PERSONA_ROL_INVALIDO: "La persona indicada no tiene el rol que corresponde",
  RECEPTOR_REQUERIDO: "Falta indicar el receptor externo",
  RECEPTOR_NO_EXISTE: "El receptor externo no existe",
  RECEPTOR_INACTIVO: "El receptor externo está inactivo",
  INTERMEDIARIO_CON_AF: "En un intermediario, ni quien paga ni quien recibe puede ser Aloja Fácil",
  ROL_Y_PUNTAS_INCOHERENTES: "El rol de Aloja Fácil no calza con quién paga y quién recibe",
  CONCEPTO_INVALIDO: "El concepto solo aplica a un movimiento con una aseadora o un trabajador",
  IMAGENES_NO_SOPORTADAS: "Por esta vía todavía no se reciben fotos",
  LAVANDERIA_NO_PERMITIDA: "Un gasto no puede apuntar a una lavandería (eso va en Lavandería)",
};

/** Todos los códigos con nombre. Si un código aparece en dos grupos, vale el último (no debería pasar: la prueba anti-deriva lo vigila). */
export const NOMBRES_CODIGO: Readonly<Record<string, string>> = {
  ...CODIGOS_DEL_ASISTENTE,
  ...CODIGOS_DE_TIPO_DE_FALLO,
  ...CODIGOS_DEL_SERVIDOR,
  ...CODIGOS_DE_VALIDACION_DEL_GASTO,
};

/**
 * Explicación en español de un código, con el código entre paréntesis (para que soporte lo reconozca); solo el código entre
 * paréntesis si no se conoce. «—» si no hay. «otro» es la marca del servidor para un código que no calza con ninguno conocido.
 */
export function textoCodigo(codigo: string | null | undefined): string {
  if (typeof codigo !== "string" || codigo === "") return "—";
  if (codigo === "otro") return NOMBRE_OTRO;
  if (tiene(NOMBRES_CODIGO, codigo)) return `${NOMBRES_CODIGO[codigo]} (${codigo})`;
  return `(${codigo})`;
}

// ─── El detalle del evento (claves en español) ──────────────────────────────────────────────────────────────────

/**
 * Las claves del detalle que el servidor deja pasar (backend/src/services/botEventos.js, `sanearDetalleEvento`): todo lo
 * demás se descarta allá. Cada una con su nombre, en el orden en que se muestran.
 */
export const NOMBRES_CLAVE_DETALLE: Readonly<Record<keyof BotEventoDetalle, string>> = {
  messageSid: "Código del mensaje (Twilio)",
  fotos: "Fotos que venían en el mensaje",
  modelos: "Modelos de IA que se usaron",
  guardadoEnEsteTurno: "¿Terminó un guardado en este mensaje?",
  ajustesDelPanel: "¿Usó los ajustes del panel?",
  agregado: "¿Resume varios mensajes?",
  cantidad: "Mensajes que resume",
  ventanaSeg: "Tiempo que abarcan",
  usuariosEnConflicto: "Cuentas que comparten el teléfono",
};

/** Cómo se rotula la fila que junta `agregado`, `cantidad` y `ventanaSeg` («37 mensajes en 5 minutos»). */
export const ETIQUETA_MENSAJES_AGRUPADOS = "Mensajes agrupados";

const plural = (n: number, singular: string, varios: string): string => `${n} ${n === 1 ? singular : varios}`;

/** «menos de 1 minuto», «5 minutos», «1 h 30 min»: la duración de un resumen, para quien no piensa en segundos. */
export function textoVentana(segundos: number): string {
  if (segundos < 60) return "menos de 1 minuto";
  const minutos = Math.round(segundos / 60);
  if (minutos < 60) return plural(minutos, "minuto", "minutos");
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto === 0 ? plural(horas, "hora", "horas") : `${horas} h ${resto} min`;
}

/**
 * «37 mensajes en 5 minutos» para una fila que el asistente agrupó (los bloqueos de gente que no es del equipo se anotan juntos, uno por
 * ventana de 5 minutos: twilio/src/libs/bloqueosAgregados.js). `null` si el evento no es un resumen o no trae la cantidad: una fila común
 * es un mensaje y no necesita decirlo.
 */
export function resumenDeAgregado(detalle: BotEventoDetalle | null | undefined): string | null {
  if (!detalle || detalle.agregado !== true || typeof detalle.cantidad !== "number") return null;
  const mensajes = plural(detalle.cantidad, "mensaje", "mensajes");
  return typeof detalle.ventanaSeg === "number" ? `${mensajes} en ${textoVentana(detalle.ventanaSeg)}` : mensajes;
}

export interface FilaDeDetalle {
  clave: keyof BotEventoDetalle;
  etiqueta: string;
  valor: string;
}

/**
 * Las filas del detalle de un evento, con nombre en español y el valor ya redactado. Solo salen las claves que vienen;
 * `simulado` aclara el «guardado»: en una simulación no se escribió nada de verdad. Un resumen de mensajes (`agregado` + `cantidad`) sale
 * en UNA fila («37 mensajes en 5 minutos») y no en tres sueltas; las que no se pueden juntar salen cada una con su nombre.
 */
export function filasDelDetalle(detalle: BotEventoDetalle | null | undefined, simulado: boolean): FilaDeDetalle[] {
  if (!detalle) return [];
  const filas: FilaDeDetalle[] = [];
  const agregar = (clave: keyof BotEventoDetalle, valor: string) => filas.push({ clave, etiqueta: NOMBRES_CLAVE_DETALLE[clave], valor });
  if (typeof detalle.messageSid === "string" && detalle.messageSid !== "") agregar("messageSid", detalle.messageSid);
  if (typeof detalle.fotos === "number") agregar("fotos", String(detalle.fotos));
  if (Array.isArray(detalle.modelos) && detalle.modelos.length > 0) agregar("modelos", detalle.modelos.join(", "));
  if (typeof detalle.guardadoEnEsteTurno === "boolean") {
    agregar("guardadoEnEsteTurno", detalle.guardadoEnEsteTurno ? (simulado ? "Sí, simulado (no se guardó nada de verdad)" : "Sí") : "No");
  }
  if (typeof detalle.ajustesDelPanel === "boolean") {
    agregar("ajustesDelPanel", detalle.ajustesDelPanel ? "Sí" : "No (usó los valores de fábrica)");
  }
  const resumen = resumenDeAgregado(detalle);
  if (resumen !== null) {
    filas.push({ clave: "cantidad", etiqueta: ETIQUETA_MENSAJES_AGRUPADOS, valor: resumen });
  } else {
    if (typeof detalle.agregado === "boolean") agregar("agregado", detalle.agregado ? "Sí" : "No");
    if (typeof detalle.cantidad === "number") agregar("cantidad", String(detalle.cantidad));
    if (typeof detalle.ventanaSeg === "number") agregar("ventanaSeg", textoVentana(detalle.ventanaSeg));
  }
  if (Array.isArray(detalle.usuariosEnConflicto) && detalle.usuariosEnConflicto.length > 0) {
    agregar("usuariosEnConflicto", detalle.usuariosEnConflicto.map((id) => `#${id}`).join(", "));
  }
  return filas;
}
